import { EMPTY_TABLE_DATA } from 'constants/utils';
import { useAppContext } from 'contexts/AppContext';
import { Installation } from 'interfaces';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TicketService } from 'services/Tickets/TicketService';
import { TicketsResponse } from 'typings';
import { TableDataModel } from 'typings/components/TableDataModel';

interface UseTicketsReturn {
    ticketsInQueue: TableDataModel<TicketsResponse['tickets']>;
    ticketsInAttendance: TableDataModel<TicketsResponse['tickets']>;
    isLoadingTicketsInAttendance: boolean;
    isLoadingTicketsInQueue: boolean;
    setTicketsInQueue: Dispatch<SetStateAction<TableDataModel<TicketsResponse['tickets']>>>;
    setTicketsInAttendance: Dispatch<SetStateAction<TableDataModel<TicketsResponse['tickets']>>>;
    handleFetchTicketsInAttendance: () => Promise<void>;
    handleFetchTicketsInQueue: () => Promise<void>;
    fetchAllTickets: () => Promise<void>;
    canFetchTickets: boolean;
}

interface UseTicketsProps {
    appliedInstallations: Installation[];
    selectedNumberOptions: Installation[] | undefined;
}

export const useTickets = ({ appliedInstallations, selectedNumberOptions }: UseTicketsProps): UseTicketsReturn => {
    const {
        appServices: { reportService },
    } = useAppContext();
    const botsToFetch = useMemo(
        () => selectedNumberOptions ?? appliedInstallations,
        [selectedNumberOptions, appliedInstallations],
    );

    const abortControllerRef = useRef<AbortController | null>(null);

    const [ticketsInQueue, setTicketsInQueue] = useState<TableDataModel<TicketsResponse['tickets']>>(EMPTY_TABLE_DATA);
    const [ticketsInAttendance, setTicketsInAttendance] =
        useState<TableDataModel<TicketsResponse['tickets']>>(EMPTY_TABLE_DATA);
    const [isLoadingTicketsInAttendance, setIsLoadingTicketsInAttendance] = useState(false);
    const [isLoadingTicketsInQueue, setIsLoadingTicketsInQueue] = useState(false);

    const ticketService = useMemo(() => new TicketService(reportService), [reportService]);

    const canFetchTickets = useMemo(() => ticketService.canFetchTickets(botsToFetch), [ticketService, botsToFetch]);

    useEffect(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        setIsLoadingTicketsInAttendance(false);
        setIsLoadingTicketsInQueue(false);

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [botsToFetch]);

    const handleFetchTicketsInAttendance = useCallback(async () => {
        if (!canFetchTickets || !abortControllerRef.current) return;

        await ticketService.fetchTicketsInAttendance(
            { bots: botsToFetch, abortController: abortControllerRef.current },
            setIsLoadingTicketsInAttendance,
            setTicketsInAttendance,
        );
    }, [ticketService, botsToFetch, canFetchTickets]);

    const handleFetchTicketsInQueue = useCallback(async () => {
        if (!canFetchTickets || !abortControllerRef.current) return;

        await ticketService.fetchTicketsInQueue(
            { bots: botsToFetch, abortController: abortControllerRef.current },
            setIsLoadingTicketsInQueue,
            setTicketsInQueue,
        );
    }, [ticketService, botsToFetch, canFetchTickets]);

    const fetchAllTickets = useCallback(async () => {
        if (!canFetchTickets || !abortControllerRef.current) return;

        await ticketService.fetchAllTickets(
            { bots: botsToFetch, abortController: abortControllerRef.current },
            setIsLoadingTicketsInAttendance,
            setTicketsInAttendance,
            setIsLoadingTicketsInQueue,
            setTicketsInQueue,
        );
    }, [ticketService, botsToFetch, canFetchTickets]);

    return {
        ticketsInQueue,
        ticketsInAttendance,
        isLoadingTicketsInAttendance,
        isLoadingTicketsInQueue,
        setTicketsInQueue,
        setTicketsInAttendance,
        handleFetchTicketsInAttendance,
        handleFetchTicketsInQueue,
        fetchAllTickets,
        canFetchTickets,
    };
};
